import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useConfig } from 'wagmi';
import { formatUnits, type Address } from 'viem';
import { getBalance } from 'viem/actions';
import { ink } from '../../utils/chains';
import { getRelayTokenPrice } from '../../hooks/useRelayPrice';
import {
  fetchRelayWalletTokens,
  fetchSupabaseEvmTokenMeta,
  fetchSupabaseSolTokenMeta
} from '../../utils/investorPortfolio';
import { getAllInvestors } from '../../utils/supabase';

const Page = styled.div`
  padding: 24px 20px 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 16px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
`;

const ValueGreen = styled(Value)`
  color: #10b981;
`;

const LoadingText = styled.div`
  font-size: 13px;
  color: #a1a1aa;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
`;

const RefreshButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #333333;
  background: #111111;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
    background: #1a1a1a;
  }
`;

const ETH_NATIVE_CHAINS = [
  { chainId: ink.id, name: 'Ink' },
  { chainId: 1, name: 'Ethereum' },
  { chainId: 8453, name: 'Base' },
  { chainId: 10, name: 'Optimism' },
  { chainId: 42161, name: 'Arbitrum' },
];

const formatCurrency = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isSolNative = (token: any) => {
  const address = (token.address || '').toString();
  const normalized = address.toLowerCase();
  return (
    token.symbol === 'SOL' ||
    address === 'So11111111111111111111111111111111111111111' ||
    address === '11111111111111111111111111111111' ||
    normalized === 'native' ||
    normalized === '0x0000000000000000000000000000000000000000'
  );
};

const fetchSolanaTotals = async (svmAddress: string | null) => {
  if (!svmAddress) {
    return { solValue: 0, slotValue: 0 };
  }

  try {
    const relayTokens = await fetchRelayWalletTokens(svmAddress, [792703809], true);
    const solMints = relayTokens.map((token) => token.address);
    const solMeta = await fetchSupabaseSolTokenMeta(solMints);
    let solValue = 0;
    let slotValue = 0;

    relayTokens.forEach((token) => {
      if (token.chainId !== 792703809) return;
      const valueUSD = token.valueUSD || 0;

      if (isSolNative(token)) {
        solValue += valueUSD;
        return;
      }

      if (solMeta.has(token.address)) {
        slotValue += valueUSD;
      }
    });

    return { solValue, slotValue };
  } catch (error) {
    console.warn('[Admin] Failed to fetch Solana totals', error);
    return { solValue: 0, slotValue: 0 };
  }
};

const InvestorAdminDashboardPWA: React.FC = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const adminName = localStorage.getItem('investorAdminName') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalFunding, setTotalFunding] = useState(0);
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(0);
  const [investorCount, setInvestorCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!adminName) {
      navigate('/investor-pwa/admin', { replace: true });
    }
  }, [adminName, navigate]);

  useEffect(() => {
    const fetchTotals = async () => {
      setLoading(true);
      setError('');
      setTotalFunding(0);
      setTotalPortfolioValue(0);

      const investorResult = await getAllInvestors();
      if (!investorResult.success || !investorResult.investors) {
        setError(investorResult.error || 'Failed to load investors.');
        setLoading(false);
        return;
      }

      const investors = investorResult.investors;
      setInvestorCount(investors.length);

      const fundingSum = investors.reduce((sum, investor) => sum + (investor.funding || 0), 0);
      setTotalFunding(fundingSum);

      let portfolioSum = 0;

      for (const investor of investors) {
        let evmTotal = 0;
        if (investor.evm_address) {
          const evmAddress = investor.evm_address as Address;
          await Promise.all(
            ETH_NATIVE_CHAINS.map(async ({ chainId }) => {
              try {
                const client = config.getClient({ chainId });
                if (!client) return;
                const nativeBalance = await getBalance(client, { address: evmAddress });
                if (nativeBalance === 0n) return;
                const price = await getRelayTokenPrice('0x0000000000000000000000000000000000000000', chainId);
                const amount = parseFloat(formatUnits(nativeBalance, 18));
                if (price) {
                  evmTotal += amount * price;
                }
              } catch (err) {
                console.warn('[Admin] Native balance fetch failed', chainId, err);
              }
            })
          );

          const walletTokens = await fetchRelayWalletTokens(
            investor.evm_address,
            ETH_NATIVE_CHAINS.map((chain) => chain.chainId),
            false
          );
          const meta = await fetchSupabaseEvmTokenMeta(walletTokens.map((token) => token.address));
          walletTokens.forEach((token) => {
            if (token.balanceRaw <= 0n) return;
            if (!meta.has(token.address.toLowerCase())) return;
            evmTotal += token.valueUSD || 0;
          });
        }

        const solTotals = await fetchSolanaTotals(investor.svm_address || null);

        portfolioSum += evmTotal + solTotals.solValue + solTotals.slotValue;
      }

      setTotalPortfolioValue(portfolioSum);
      setLoading(false);
    };

    fetchTotals();
  }, [config, refreshKey]);

  const roiPercent = useMemo(() => {
    if (!totalFunding || totalFunding <= 0) return null;
    return (totalPortfolioValue / totalFunding) * 100;
  }, [totalFunding, totalPortfolioValue]);

  return (
    <Page>
      <Title>Admin Dashboard</Title>
      <RefreshButton onClick={() => setRefreshKey(prev => prev + 1)}>Refresh Totals</RefreshButton>
      <Card>
        <Label>Total Managed</Label>
        {loading ? <LoadingText>Loading...</LoadingText> : <ValueGreen>${formatCurrency(totalFunding)}</ValueGreen>}
        <Label>Total Portfolio Value</Label>
        {loading ? <LoadingText>Loading...</LoadingText> : <Value>${formatCurrency(totalPortfolioValue)}</Value>}
        <Label>ROI to Date</Label>
        {loading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <ValueGreen>{roiPercent === null ? '—' : `${roiPercent.toFixed(2)}%`}</ValueGreen>
        )}
        <Label>Active Investors</Label>
        {loading ? <LoadingText>Loading...</LoadingText> : <Value>{investorCount}</Value>}
        {error && <ErrorText>{error}</ErrorText>}
      </Card>
    </Page>
  );
};

export default InvestorAdminDashboardPWA;
